/**
 * My mail preferences in this workspace (one record on the server, so every device follows them): Settings →
 * Notifications → Mail (logic/mail mailNotifies), my new-mail notifications — on or off, Priority mail (the default)
 * and which tabs notify whatever their priority — and Settings → Email → "Open messages in", the original email (the
 * default) or its reading view. Changes save at once (optimistic; a refused save reloads). The tab switches follow the server's strip (MailTabDef): the one Mail cached on the device, or
 * one inbox listing's when Mail hasn't opened yet.
 */
import type { MailBodyView, MailNotifyPrefs, MailTabDef, MailTabId } from "@bundle/contracts";
import { DEFAULT_MAIL_NOTIFY_PREFS, categoryTabs, mailBodyView } from "../logic/mail.ts";
import { cachedMailTabs, mailTabsKey } from "./MailboxViewModel.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type MailNotifyState = {
  status: "loading" | "ready" | "failed";
  prefs: MailNotifyPrefs;
  /** Whether Priority mail notifies. */
  priority: boolean;
  /** How messages open (the original by default). */
  view: MailBodyView;
  /** Every category tab, in order, with whether it notifies. */
  tabs: { tab: MailTabId; def: MailTabDef; on: boolean }[];
  saving: boolean;
};

export class MailNotifyViewModel extends ViewModel<MailNotifyState> {
  private defs: MailTabDef[];

  constructor(private readonly ws: WorkspaceViewModel) {
    const defs = cachedMailTabs(ws.deps.storage.get(mailTabsKey(ws.wsId)));
    super({ status: "loading", ...shown({ ...DEFAULT_MAIL_NOTIFY_PREFS }, defs), saving: false });
    this.defs = defs;
  }

  async refresh(): Promise<void> {
    try {
      const [prefs] = await Promise.all([this.ws.deps.api.mailNotifyPrefs(this.ws.wsId), this.defs.length === 0 ? this.fetchTabs() : undefined]);
      if (!this.isDisposed) this.update({ status: "ready", ...shown(prefs, this.defs) });
    } catch {
      if (!this.isDisposed && this.state.status === "loading") this.update({ status: "failed" });
    }
  }

  setEnabled(enabled: boolean): void {
    this.save({ ...this.state.prefs, enabled });
  }

  setPriority(priority: boolean): void {
    this.save({ ...this.state.prefs, priority });
  }

  /** How messages open on every device: the original email or its reading view (each message can still switch). */
  setView(view: MailBodyView): void {
    this.save({ ...this.state.prefs, view });
  }

  setTab(tab: MailTabId, on: boolean): void {
    const tabs = on ? [...new Set([...this.state.prefs.tabs, tab])] : this.state.prefs.tabs.filter((t) => t !== tab);
    this.save({ ...this.state.prefs, tabs: categoryTabs(this.defs).map((d) => d.id).filter((t) => tabs.includes(t)) });
  }

  /** The strip from my first mailbox's inbox (Mail caches it the same way). No mailbox: no tab switches. */
  private async fetchTabs(): Promise<void> {
    try {
      const [box] = await this.ws.deps.api.listMailboxes(this.ws.wsId);
      if (box === undefined) return;
      const r = await this.ws.deps.api.listMailThreads(this.ws.wsId, box.id, { folder: "inbox", limit: 1 });
      this.defs = r.tabDefs;
      this.ws.deps.storage.set(mailTabsKey(this.ws.wsId), JSON.stringify(r.tabDefs));
    } catch {
      // The switches wait for Mail; the rest of the screen works.
    }
  }

  private save(prefs: MailNotifyPrefs): void {
    this.update({ ...shown(prefs, this.defs), saving: true });
    this.ws.deps.api.setMailNotifyPrefs(this.ws.wsId, prefs).then(
      (saved) => {
        if (!this.isDisposed) this.update({ ...shown(saved, this.defs), saving: false });
      },
      () => {
        if (this.isDisposed) return;
        this.update({ saving: false });
        void this.refresh();
      },
    );
  }
}

const shown = (p: MailNotifyPrefs, defs: readonly MailTabDef[]) => ({
  prefs: p,
  priority: p.priority !== false,
  view: mailBodyView(p),
  tabs: categoryTabs(defs).map((def) => ({ tab: def.id, def, on: p.tabs.includes(def.id) })),
});
